import React from 'react';
import { Search, X, Loader2, Check } from 'lucide-react';
import { Link } from 'react-router-dom';
import Modal from '../ui/Modal';
import Button from '../ui/Button';
import { ISegment } from '../../types/segments/segments.types';

interface AddToSegmentModalProps {
  isOpen: boolean;
  onClose: () => void;
  segments: ISegment[];
  segmentsLoading: boolean;
  addContactsToSegmentLoading: boolean;
  selectedSegmentId: string | null;
  onSegmentSelect: (segmentId: string | null) => void;
  onConfirm: () => void;
  segmentSearch: string;
  onSegmentSearchChange: (search: string) => void;
  contactCount: number;
}

const AddToSegmentModal: React.FC<AddToSegmentModalProps> = ({
  isOpen,
  onClose,
  segments,
  segmentsLoading,
  addContactsToSegmentLoading,
  selectedSegmentId,
  onSegmentSelect,
  onConfirm,
  segmentSearch,
  onSegmentSearchChange,
  contactCount,
}) => {
  const manualSegments = segments.filter((s) => s.type === 'Manual');

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Add to Manual Segment">
      <p className="text-gray-600 mb-4">
        Select a manual segment to add {contactCount} selected contact(s) to:
      </p>

      {/* Search Bar */}
      <div className="mb-4">
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Search className="h-5 w-5 text-gray-400" />
          </div>
          <input
            type="text"
            placeholder="Search segments..."
            value={segmentSearch}
            disabled={segmentsLoading || manualSegments.length === 0}
            onChange={(e) => onSegmentSearchChange(e.target.value)}
            className="block w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg bg-white text-gray-900 placeholder-gray-500 outline-none focus:ring-1 focus:ring-primary focus:border-primary"
          />
          {segmentSearch && (
            <button
              onClick={() => onSegmentSearchChange('')}
              className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>

      {segmentsLoading ? (
        <div className="text-center py-4">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
          <p className="text-gray-500 mt-2">Loading segments...</p>
        </div>
      ) : manualSegments.length === 0 ? (
        <div className="text-center py-4">
          <p className="text-gray-500 mb-4">
            {segmentSearch
              ? 'No manual segments found matching your search.'
              : 'No manual segments found. Please create a manual segment first.'}
          </p>
          {!segmentSearch && (
            <Link
              to="/segments"
              onClick={onClose}
              className="inline-flex items-center justify-center rounded-lg font-medium transition-colors px-4 py-2 text-sm bg-primary text-primary-foreground hover:bg-primary-hover focus:outline-none focus:ring-0"
            >
              Go to Segments
            </Link>
          )}
        </div>
      ) : (
        <div className="max-h-64 overflow-y-auto mb-6 space-y-2">
          {manualSegments.map((segment) => {
            const isSelected = selectedSegmentId === segment._id;
            return (
              <button
                key={segment._id}
                onClick={() => onSegmentSelect(segment._id || null)}
                disabled={segmentsLoading}
                aria-pressed={isSelected}
                aria-label={`Select ${segment.name} segment`}
                className={`
                    w-full text-left p-3 rounded-lg border transition-all duration-200 ring-none
                    disabled:opacity-50 disabled:cursor-not-allowed
                    ${
                      isSelected
                        ? 'border-primary bg-primary/10 shadow-sm'
                        : 'border-border bg-white hover:border-primary hover:bg-primary/10'
                    }
                  `}
              >
                <div className="flex items-center gap-3">
                  <div
                    className={`
                        flex-shrink-0 w-5 h-5 rounded-full border-2 flex items-center justify-center
                        transition-all duration-200
                        ${
                          isSelected
                            ? 'border-primary bg-primary'
                            : 'border-border bg-white group-hover:border-primary'
                        }
                      `}
                    aria-hidden="true"
                  >
                    {isSelected && (
                      <Check
                        className="w-3 h-3 text-primary-foreground transition-colors"
                        strokeWidth={3}
                      />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div
                      className={`
                          font-medium text-sm
                          ${isSelected ? 'text-primary transition-colors' : 'text-foreground'}
                        `}
                    >
                      {segment.name}
                    </div>
                    {segment.description && (
                      <div className="text-xs text-muted-foreground mt-0.5 line-clamp-1">
                        {segment.description}
                      </div>
                    )}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      )}
      <div className="flex justify-end gap-3">
        <Button
          variant="outline"
          onClick={onClose}
          disabled={segmentsLoading}
          className="ring-none outline-none"
        >
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={onConfirm}
          disabled={
            segmentsLoading || addContactsToSegmentLoading || !selectedSegmentId
          }
          className="flex items-center gap-2 ring-none outline-none"
        >
          {addContactsToSegmentLoading && (
            <Loader2 className="w-4 h-4 animate-spin" />
          )}
          {addContactsToSegmentLoading ? 'Adding...' : 'Add to Segment'}
        </Button>
      </div>
    </Modal>
  );
};

export default AddToSegmentModal;
